'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface StoryCardProps {
  story: {
    id: number;
    title: string;
    creator: string;
    category: string;
    language: string;
    duration: string;
    plays: string;
    rating: number;
    image: string;
    alt: string;
    description: string;
    mood: string;
  };
  onPlay: (storyId: number) => void;
  onBookmark: (storyId: number) => void;
  isBookmarked: boolean;
}

const StoryCard = ({
  story,
  onPlay,
  onBookmark,
  isBookmarked,
}: StoryCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayClick = () => {
    setIsPlaying(!isPlaying);
    onPlay(story.id);
  };

  return (
    <div
      className="bg-card rounded-xl shadow-warm overflow-hidden transition-all duration-300 hover:shadow-warm-lg hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={story.image}
          alt={story.alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent" />

        <div className="absolute top-3 right-3 flex items-center space-x-2">
          <button
            onClick={() => onBookmark(story.id)}
            className="p-2 bg-card/90 backdrop-blur-sm rounded-full hover:bg-primary hover:text-primary-foreground transition-all duration-200"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <Icon
              name="BookmarkIcon"
              size={20}
              variant={isBookmarked ? 'solid' : 'outline'}
            />
          </button>
        </div>

        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              {story.category}
            </span>
            <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full">
              {story.language}
            </span>
          </div>

          <button
            onClick={handlePlayClick}
            className={`p-3 rounded-full transition-all duration-300 ${
              isPlaying
                ? 'bg-primary text-primary-foreground animate-pulse'
                : 'bg-card/90 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground'
            }`}
            aria-label={isPlaying ? 'Pause story' : 'Play story'}
          >
            <Icon
              name={isPlaying ? 'PauseIcon' : 'PlayIcon'}
              size={20}
              variant="solid"
            />
          </button>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-heading font-bold text-text-primary mb-1 line-clamp-1">
          {story.title}
        </h3>

        <p className="text-sm text-text-secondary mb-3 line-clamp-2">
          {story.description}
        </p>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="UserIcon" size={16} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">{story.creator}</span>
          </div>

          <div className="flex items-center space-x-1">
            <Icon name="StarIcon" size={16} className="text-accent" variant="solid" />
            <span className="text-sm font-medium text-text-primary">
              {story.rating}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="ClockIcon" size={14} />
            <span>{story.duration}</span>
          </div>

          <div className="flex items-center space-x-1">
            <Icon name="PlayIcon" size={14} />
            <span>{story.plays} plays</span>
          </div>

          <div className="flex items-center space-x-1">
            <Icon name="HeartIcon" size={14} />
            <span>{story.mood}</span>
          </div>
        </div>

        {isHovered && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2">
              <button className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-muted text-text-primary rounded-lg text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-200">
                <Icon name="ShareIcon" size={16} />
                <span>Share</span>
              </button>

              <button className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-muted text-text-primary rounded-lg text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-200">
                <Icon name="InformationCircleIcon" size={16} />
                <span>Details</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryCard;